<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Danh sách phiếu đề nghị mua hàng'">
        <template v-slot:toolbar>
          <b-button
            v-if="checkPermission('PR_INSERT')"
            variant="primary"
            size="sm"
            @click="addNew"
          >
            <i class="fas fa-plus"></i> Tạo phiếu
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filters -->
          <b-row class="mb-3">
            <b-col cols="2">
              <b-form-select
                v-model="filterStatus"
                :options="statusOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="2">
              <BDropdownCustom
                v-model="filterDepartmentId"
                :options="departmentDropdownOptions"
                :placeholder="'phòng ban'"
                :matchKey="['name']"
                :searchable="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="2">
              <b-form-select
                v-model="filterCostItemId"
                :options="costItemOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="2">
              <BDropdownCustom
                v-model="filterStoreId"
                :options="storeDropdownOptions"
                :placeholder="'cửa hàng'"
                :matchKey="['name']"
                :searchable="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="2">
              <date-picker
                v-model="filterPeriod"
                :config="dpConfigs.month"
                class="form-control-sm"
                placeholder="Chọn tháng"
              ></date-picker>
            </b-col>
            <b-col cols="2">
              <b-form-select
                v-model="filterCompanyId"
                :options="companyOptions"
                size="sm"
              />
            </b-col>
          </b-row>
          <b-row class="mb-5">
            <b-col cols="2">
              <b-form-input
                v-model="filterCode"
                size="sm"
                placeholder="Tìm theo mã phiếu"
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="4">
              <b-form-input
                v-model="filterDescription"
                size="sm"
                placeholder="Từ khóa diễn giải..."
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="4">
              <Autosuggest
                :model.sync="employeeModel"
                :suggestions="employeeSuggestions"
                :limit="suggestLimit"
                suggestionName="suggestionName"
                placeholder="người tạo"
                @select="onSelectedEmployee"
                @change="onSearchEmployeeChange"
              />
            </b-col>
            <b-col cols="2" class="d-flex align-items-center">
              <b-form-checkbox
                v-model="filterMyApproval"
                class="mr-3"
                @change="onFilter"
              >
                Cần tôi duyệt
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="12">
              <b-button variant="primary" size="sm" @click="onFilter">
                <i class="fas fa-search"></i> Lọc
              </b-button>
            </b-col>
          </b-row>

          <!-- Table -->
          <b-table
            :items="items"
            :fields="fields"
            bordered
            hover
            :busy="onLoading"
          >
            <template #cell(index)="data">
              {{ (page - 1) * 20 + data.index + 1 }}
            </template>
            <template #cell(code)="data">
              <a href="#" @click.prevent="viewDetail(data.item)">
                <strong>{{ data.value }}</strong>
              </a>
            </template>
            <template #cell(period)="data">
              {{ formatPeriod(data.value) }}
            </template>
            <template #cell(info)="row">
              <div>
                <div>
                  <span class="font-weight-bold">Doanh nghiệp:</span>
                  <span class="ml-1">{{
                    row.item.companyName || row.item.companyId
                  }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Phòng ban:</span>
                  <span class="ml-1">{{ getDepartmentName(row.item) }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Khoản mục:</span>
                  <span class="ml-1">{{ getCostItemName(row.item) }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Cửa hàng:</span>
                  <span class="ml-1">{{ getStoreName(row.item) }}</span>
                </div>
              </div>
            </template>
            <template #cell(requestedAmount)="data">
              {{ convertPrice(data.value) }}
            </template>
            <template #cell(budgetRemainingAfterPR)="data">
              <span
                :class="
                  data.value < 0
                    ? 'text-danger font-weight-bold'
                    : 'text-success'
                "
              >
                {{ data.value !== null ? convertPrice(data.value) : '--' }}
              </span>
            </template>
            <template #cell(status)="data">
              <b-badge
                :variant="prStatusVariant(data.value)"
                style="cursor: pointer"
                @click="openApprovalWorkflow(data.item)"
              >
                {{ prStatusName(data.value) }}
              </b-badge>
            </template>
            <template #cell(nextApprovalStep)="data">
              <span>
                {{
                  data.value && data.value.level
                    ? `${data.value.statusName || ''} bước ${data.value.level}`
                    : '--'
                }}
              </span>
            </template>
            <template #cell(createdBy)="row">
              {{ getEmployeeName(row.item) }}
            </template>
            <template #cell(createdAt)="data">
              {{ formatDateTime(data.value) }}
            </template>
            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" id="dropdown-left" no-caret right>
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="viewDetail(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Xem chi tiết
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showHistory(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-time icon-size-sm"></i>
                      &nbsp; Lịch sử
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canApprove"
                    @click="approveItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-checkmark icon-size-sm"></i>
                      &nbsp; Duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canApprove"
                    @click="rejectItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-cross icon-size-sm"></i>
                      &nbsp; Từ chối
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      canDeleteItem(row.item) && checkPermission('PR_UPDATE')
                    "
                    @click="openDeleteModal(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="fas fa-trash-alt icon-size-sm"></i>
                      &nbsp; Xoá phiếu
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      canCancelItem(row.item)
                    "
                    @click="cancelItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-cancel icon-size-sm"></i>
                      &nbsp; Hủy
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col cols="12" md="4">
              Tổng số: <strong>{{ totalRow }}</strong>
            </b-col>
            <b-col cols="12" md="8" class="d-flex justify-content-end">
              <BPaginationNavCustom
                :currentPage="page"
                :numberOfPages="numberOfPage"
                :totalPage="numberOfPage"
                :linkParams="paginationLinkParams"
                @page-change="fetchData"
              />
            </b-col>
          </b-row>

          <!-- Modal xóa phiếu (chờ duyệt) -->
          <b-modal
            ref="deleteModal"
            title="Xoá phiếu đề nghị mua hàng"
            @ok="confirmDelete"
            ok-variant="danger"
            ok-title="Xoá"
            cancel-title="Đóng"
          >
            <p class="mb-0">
              Bạn có chắc muốn xoá phiếu <strong>{{ deleteForm.code }}</strong
              >?
            </p>
          </b-modal>

          <!-- Modal hủy phiếu -->
          <b-modal
            ref="cancelModal"
            title="Hủy phiếu đề nghị mua hàng"
            @ok="confirmCancel"
            ok-variant="danger"
            ok-title="Xác nhận hủy"
            cancel-title="Đóng"
          >
            <p>
              Bạn có chắc muốn hủy phiếu <strong>{{ cancelForm.code }}</strong
              >?
            </p>
            <b-form-group>
              <template #label>
                Lý do hủy <span class="text-danger">*</span>
              </template>
              <b-form-textarea v-model="cancelForm.reason" rows="3" required />
            </b-form-group>
          </b-modal>

          <!-- Modal duyệt -->
          <b-modal
            ref="approveModal"
            title="Phê duyệt phiếu"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Phê duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="approveForm.note"
                rows="2"
                placeholder="Nhập ghi chú duyệt"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal từ chối -->
          <b-modal
            ref="rejectModal"
            title="Từ chối phiếu"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Từ chối"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do từ chối <span class="text-danger">*</span>
              </template>
              <b-form-textarea
                v-model="rejectForm.rejectionNote"
                rows="3"
                required
              />
            </b-form-group>
          </b-modal>

          <PurchaseRequestApprovalModal
            ref="approvalModal"
            :requestId="approvalModalRequestId"
          />

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="historyItems"
            :fields="historyFields"
          />
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
  xoa_dau,
} from '@/utils/common';
import moment from 'moment';
import timeUtils from '@/utils/date';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import PurchaseRequestApprovalModal from './components/PurchaseRequestApprovalModal.vue';
import PurchaseRequestHistoryModal from './components/PurchaseRequestHistoryModal.vue';
import {
  PR_STATUS,
  PR_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import { PR_STATUS_NAME, HISTORY_ACTION_TYPE_CODE } from '@/utils/enum-name';

export default {
  components: {
    KTCodePreview,
    BDropdownCustom,
    BPaginationNavCustom,
    Autosuggest,
    PurchaseRequestApprovalModal,
    PurchaseRequestHistoryModal,
  },
  data() {
    return {
      PR_STATUS,
      items: [],
      onLoading: false,
      page: 1,
      numberOfPage: 1,
      totalRow: 0,
      filterStatus: null,
      filterDepartmentId: [],
      filterCostItemId: null,
      filterStoreId: [],
      filterCompanyId: null,
      filterPeriod: '',
      filterCode: '',
      filterDescription: '',
      filterCreatedBy: null,
      filterMyApproval: false,
      employeeModel: '',
      suggestLimit: 50,
      employeeSource: [],
      employeeSuggestions: [],
      departmentDropdownOptions: [],
      costItemOptions: [{ value: null, text: '-- Khoản mục --' }],
      storeDropdownOptions: [],
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      approvalModalRequestId: null,
      statusOptions: [
        { value: null, text: '-- Trạng thái --' },
        { value: PR_STATUS.PENDING, text: PR_STATUS_NAME[PR_STATUS.PENDING] },
        { value: PR_STATUS.BROWSING, text: PR_STATUS_NAME[PR_STATUS.BROWSING] },
        { value: PR_STATUS.APPROVED, text: PR_STATUS_NAME[PR_STATUS.APPROVED] },
        { value: PR_STATUS.REJECTED, text: PR_STATUS_NAME[PR_STATUS.REJECTED] },
        { value: PR_STATUS.CANCELED, text: PR_STATUS_NAME[PR_STATUS.CANCELED] },
      ],
      currentEmployeeId: null,
      cancelForm: { id: null, code: '', reason: '' },
      deleteForm: { id: null, code: '' },
      approveForm: { id: null, note: '' },
      rejectForm: { id: null, rejectionNote: '' },
      historyItems: [],
      historyFields: [
        { key: 'createdAt', label: 'Lúc', thStyle: { width: '15%' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '15%' } },
        { key: 'description', label: 'Chi tiết' },
        {
          key: 'updatedBy',
          label: 'Người thực hiện',
          thStyle: { width: '20%' },
        },
      ],
      dpConfigs: {
        // Used for month-only filtering on `period` (send `${filterPeriod}-01` to BE)
        month: {
          ...timeUtils.DP_CONFIG.date,
          format: 'YYYY-MM',
          viewMode: 'months',
        },
      },
      fields: [
        {
          key: 'index',
          label: 'STT',
          thStyle: { textAlign: 'center', width: '3%' },
          class: 'text-center',
        },
        {
          key: 'code',
          label: 'Mã phiếu',
          thStyle: { textAlign: 'center', width: '10%' },
        },
        {
          key: 'period',
          label: 'Kỳ ngân sách',
          thStyle: { textAlign: 'center', width: '6%' },
          class: 'text-center',
        },
        {
          key: 'info',
          label: 'Thông tin',
          thStyle: { textAlign: 'left', width: '22%' },
        },
        {
          key: 'requestedAmount',
          label: 'Số tiền',
          thStyle: { textAlign: 'center', width: '10%' },
          tdClass: 'text-right',
        },
        {
          key: 'budgetRemainingAfterPR',
          label: 'NS còn lại',
          thStyle: { textAlign: 'center', width: '9%' },
          tdClass: 'text-right',
        },
        {
          key: 'nextApprovalStep',
          label: 'Bước duyệt',
          thStyle: { textAlign: 'center', width: '8%' },
          class: 'text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { textAlign: 'center', width: '8%' },
          class: 'text-center',
        },
        {
          key: 'createdBy',
          label: 'Người tạo',
          thStyle: { textAlign: 'center', width: '9%' },
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          thStyle: { textAlign: 'center', width: '9%' },
          class: 'text-center',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '4%' },
          class: 'text-center',
        },
      ],
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
    };
  },
  computed: {
    paginationLinkParams() {
      return {
        status: this.filterStatus || '',
        departmentId:
          this.filterDepartmentId && this.filterDepartmentId.length
            ? this.filterDepartmentId[0].id
            : '',
        costItemId: this.filterCostItemId || '',
        storeId:
          this.filterStoreId && this.filterStoreId.length
            ? this.filterStoreId[0].id
            : '',
        companyId: this.filterCompanyId || '',
        period: this.filterPeriod || '',
        codeSearch: this.filterCode || '',
        descriptionSearch: this.filterDescription || '',
        createdBy: this.filterCreatedBy || '',
      };
    },
  },
  created() {
    const empInfoRaw = getData('employee_info');
    if (empInfoRaw) {
      try {
        const parsed = JSON.parse(empInfoRaw);
        this.currentEmployeeId = parsed?.id || parsed?._id || null;
      } catch (e) {
        this.currentEmployeeId = null;
      }
    }
    this.applyQueryFilters();
    this.fetchFilterOptions();
    this.fetchEmployees();
    this.fetchData();
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.applyQueryFilters();
      },
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Phiếu đề nghị mua hàng' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    prStatusVariant(val) {
      return PR_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    prStatusName(val) {
      return PR_STATUS_NAME[val] || val;
    },
    getDepartmentName(item) {
      return item?.departmentName || item?.departmentId || '';
    },
    getCostItemName(item) {
      return item?.costItemName || item?.costItemId || '';
    },
    getStoreName(item) {
      if (!item?.storeId) return '—';
      return item?.storeName || item?.storeId || '';
    },
    getEmployeeName(item) {
      if (!item?.createdBy) return '';
      return item?.employeeName || item?.createdBy || '';
    },
    applyQueryFilters() {
      const q = this.$route?.query || {};
      this.page = q.page || 1;

      this.filterStatus = q.status !== undefined && q.status !== '' ? q.status : null;
      this.filterCostItemId = q.costItemId ? Number(q.costItemId) : null;
      this.filterCompanyId = q.companyId ? Number(q.companyId) : null;
      this.filterCode = q.codeSearch ? String(q.codeSearch) : '';
      this.filterDescription = q.descriptionSearch ? String(q.descriptionSearch) : '';

      this.filterCreatedBy = q.createdBy ? Number(q.createdBy) : null;
      if (!this.filterCreatedBy) {
        this.employeeModel = this.employeeModel && this.employeeModel.trim() ? this.employeeModel : '';
      }

      // Period query is sent to BE as YYYY-MM-DD, but UI picker uses YYYY-MM.
      if (q.period) {
        const raw = String(q.period);
        const m = moment(raw, ['YYYY-MM', 'YYYY-MM-DD'], true);
        this.filterPeriod = m.isValid() ? m.format('YYYY-MM') : raw.slice(0, 7);
      } else {
        this.filterPeriod = '';
      }

      // Department/store in list UI use BDropdownCustom (array model). Keep id for API.
      const depId = q.departmentId ? Number(q.departmentId) : null;
      this.filterDepartmentId = depId ? [{ id: depId, name: '' }] : [];
      const storeId = q.storeId ? Number(q.storeId) : null;
      this.filterStoreId = storeId ? [{ id: storeId, name: '' }] : [];
    },
    fetchFilterOptions() {
      // Departments
      ApiService.query('departments/all').then(({ data }) => {
        const rows = data.data || [];
        this.departmentDropdownOptions = rows.map((d) => ({
          id: d.id,
          name: d.name,
        }));
        const depId =
          this.filterDepartmentId && this.filterDepartmentId.length
            ? Number(this.filterDepartmentId[0].id)
            : null;
        if (depId) {
          const found = this.departmentDropdownOptions.find((d) => Number(d.id) === depId);
          if (found) this.filterDepartmentId = [found];
        }
      });
      // Cost items
      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const rows = data.data || [];
        this.costItemOptions = [
          { value: null, text: '-- Khoản mục --' },
          ...rows.map((c) => ({ value: c.id, text: c.name })),
        ];
      });
      // Stores
      ApiService.query('stores/getStores').then(({ data }) => {
        const rows = data.data || [];
        this.storeDropdownOptions = rows.map((s) => ({
          id: s.id,
          name: s.shortName ? `${s.name} (${s.shortName})` : s.name,
          shortName: s.shortName,
        }));
        const storeId =
          this.filterStoreId && this.filterStoreId.length
            ? Number(this.filterStoreId[0].id)
            : null;
        if (storeId) {
          const found = this.storeDropdownOptions.find((s) => Number(s.id) === storeId);
          if (found) this.filterStoreId = [found];
        }
      });
      // Companies
      ApiService.get('/company').then(({ data }) => {
        const rows = data.data || [];
        this.companyOptions = [
          { value: null, text: '-- Doanh nghiệp --' },
          ...rows.map((c) => ({ value: c.id, text: c.name })),
        ];
      });
    },
    fetchEmployees() {
      ApiService.get('/employees/get-list-filter').then((response) => {
        const employees = response?.data?.data || [];
        this.employeeSource = employees.map((e) => {
          let suggestionName = e.fullName || e.name || '';
          if (e.code) suggestionName += ` (${e.code})`;
          const rawName = e.fullName || e.name || '';
          return {
            id: e.id,
            name: rawName,
            code: e.code || '',
            suggestionName,
            nameNoneSign: xoa_dau(rawName),
          };
        });
        this.employeeSuggestions = [...this.employeeSource];
        if (this.filterCreatedBy) {
          const found = this.employeeSource.find((e) => Number(e.id) === Number(this.filterCreatedBy));
          if (found) this.employeeModel = found.suggestionName;
        }
      });
    },
    onSelectedEmployee(option) {
      const item = option?.item || null;
      this.filterCreatedBy = item?.id || null;
      this.employeeModel = item ? item.suggestionName : '';
      this.employeeSuggestions = [...this.employeeSource];
    },
    onSearchEmployeeChange(text) {
      const t = text != null ? String(text) : '';
      this.employeeModel = t;
      if (!t.trim()) {
        this.filterCreatedBy = null;
        this.employeeSuggestions = [...this.employeeSource];
        return;
      }
      const tl = t.toLowerCase();
      this.employeeSuggestions = (this.employeeSource || [])
        .filter((item) => {
          return (
            (item.nameNoneSign || '').toLowerCase().indexOf(tl) > -1 ||
            (item.name || '').toLowerCase().indexOf(tl) > -1 ||
            (item.code || '').toLowerCase().indexOf(tl) > -1
          );
        })
        .slice(0, this.suggestLimit);
    },
    onFilter() {
      this.$router
        .push({
          name: 'list-purchase-requests',
          query: { ...this.paginationLinkParams, page: 1 },
        })
        .catch(() => {});
      this.fetchData();
    },
    openApprovalWorkflow(item) {
      if (!item || !item.id) return;
      this.approvalModalRequestId = item.id;
      this.$nextTick(() => {
        this.$refs.approvalModal?.open();
      });
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    showHistory(item) {
      if (!item || !item.id) return;
      ApiService.get(`v2/purchase-requests/${item.id}/history`)
        .then(({ data }) => {
          if (data.status === 200) {
            this.historyItems = (data.data || []).map((h) => ({
              ...h,
              actionLabel: this.historyActionLabel(h.action),
            }));
            this.$nextTick(() => {
              this.$refs.historyModal?.open();
            });
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err));
    },
    fetchData() {
      this.onLoading = true;
      this.page = this.$route.query.page || 1;
      const params = {
        page: this.page,
        limit: 20,
        status: this.filterStatus || '',
        departmentId:
          this.filterDepartmentId && this.filterDepartmentId.length
            ? this.filterDepartmentId[0].id
            : '',
        costItemId: this.filterCostItemId || '',
        storeId:
          this.filterStoreId && this.filterStoreId.length
            ? this.filterStoreId[0].id
            : '',
        companyId: this.filterCompanyId || '',
        codeSearch: this.filterCode || '',
        descriptionSearch: this.filterDescription || '',
        createdBy: this.filterCreatedBy || '',
        myApproval: this.filterMyApproval || '',
      };
      if (this.filterPeriod) {
        const raw = String(this.filterPeriod);
        // Expect BE `period` as YYYY-MM-DD (day fixed to 01).
        // date-picker month mode usually returns `YYYY-MM`.
        const m = moment(
          raw,
          ['YYYY-MM', 'YYYY-MM-DD', 'MM/YYYY', 'DD/MM/YYYY'],
          true,
        );
        params.period = m.isValid()
          ? m.format('YYYY-MM-DD')
          : raw.length >= 7
          ? `${raw.slice(0, 7)}-01`
          : `${raw}-01`;
      }

      ApiService.query('v2/purchase-requests', { params }).then(({ data }) => {
        if (data.status === 200) {
          this.items = data.data.dataset;
          this.numberOfPage = data.data.totalPage;
          this.totalRow = data.data.totalRow || 0;
        } else {
          this.makeToastFaile(data.message);
        }
        this.onLoading = false;
      });
    },
    addNew() {
      this.$router.push({ name: 'add-purchase-request' });
    },
    editItem(item) {
      this.$router.push({
        name: 'edit-purchase-request',
        query: { id: item.id },
      });
    },
    viewDetail(item) {
      this.$router.push({
        name: 'detail-purchase-request',
        query: { id: item.id, from: this.$route.fullPath },
      });
    },
    canDeleteItem(item) {
      if (!this.currentEmployeeId || !item?.createdBy) return false;
      if (
        item.status !== PR_STATUS.PENDING &&
        item.status !== PR_STATUS.BROWSING
      ) {
        return false;
      }
      return Number(item.createdBy) === Number(this.currentEmployeeId);
    },
    canCancelItem(item) {
      if (!item?.status) return false;
      if (
        item.status === PR_STATUS.CANCELED ||
        item.status === PR_STATUS.REJECTED
      ) {
        return false;
      }
      if (
        item.status === PR_STATUS.PENDING ||
        item.status === PR_STATUS.BROWSING
      ) {
        return false;
      }
      return true;
    },
    openDeleteModal(item) {
      this.deleteForm = { id: item.id, code: item.code || '' };
      this.$refs.deleteModal.show();
    },
    confirmDelete() {
      if (!this.deleteForm?.id) return;
      ApiService.post('v2/purchase-requests/soft-delete', {
        id: this.deleteForm.id,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    cancelItem(item) {
      this.cancelForm = { id: item.id, code: item.code, reason: '' };
      this.$refs.cancelModal.show();
    },
    confirmCancel() {
      if (!this.cancelForm.reason.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do hủy!');
        return;
      }
      ApiService.post('v2/purchase-requests/cancel', this.cancelForm)
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    approveItem(item) {
      this.approveForm = { id: item.id, note: '' };
      this.$refs.approveModal.show();
    },
    rejectItem(item) {
      this.rejectForm = { id: item.id, rejectionNote: '' };
      this.$refs.rejectModal.show();
    },
    patchPurchaseRequestChangeStatus(id, status, note) {
      return ApiService.patch(`v2/purchase-requests/change-status/${id}`, {
        status,
        note: note != null ? String(note) : '',
      });
    },
    confirmApprove() {
      if (!this.approveForm?.id) return;
      this.patchPurchaseRequestChangeStatus(
        this.approveForm.id,
        WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        this.approveForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    confirmReject() {
      if (!this.rejectForm?.id) return;
      if (
        !this.rejectForm.rejectionNote ||
        !this.rejectForm.rejectionNote.trim()
      ) {
        this.makeToastFaile('Vui lòng nhập lý do từ chối!');
        return;
      }
      this.patchPurchaseRequestChangeStatus(
        this.rejectForm.id,
        WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        this.rejectForm.rejectionNote,
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
  },
};
</script>
